import type { AxiosResponse } from "axios";
import type { Folder } from "certiblok-api-manager";
import { createContext, ReactNode, useContext, useState } from "react";
import { proxy } from "valtio";
import { proxyMap } from "valtio/utils";
import type { TreeViewListItem, TreeViewListItemType } from "./types/ListItem";
import type { TreeViewOpenedFolder } from "./types/OpenedFolder";

type TreeViewContextType = {
	itemsList: TreeViewListItem[];
	openedFolders: Map<string, TreeViewOpenedFolder>;
	fetchRootLayer: (params: { page: number; perPage: number }) => Promise<AxiosResponse<TreeViewListItemType[]>>;
	fetchFolderLayer: (params: {
		folderId: string;
		page: number;
		perPage: number;
	}) => Promise<AxiosResponse<TreeViewListItemType[]>>;
	lastSelectedFolder: { value?: Folder };
	selectedItemIds: { values: string[] };
	isSelecting: { value: boolean };
	canSwitchView?: boolean;
	rootFolderId: string;
	appliedFilters: { [key: string]: string };
	ignoreQueryParams?: boolean;
};

const TreeViewContext = createContext({} as TreeViewContextType);

export const TreeViewProvider = ({
	children,
	fetchFolderLayer,
	fetchRootLayer,
	canSwitchView,
	rootFolderId,
	appliedFilters,
	ignoreQueryParams,
}: {
	children?: ReactNode;
	fetchRootLayer: TreeViewContextType["fetchRootLayer"];
	fetchFolderLayer: TreeViewContextType["fetchFolderLayer"];
	canSwitchView?: boolean;
	rootFolderId: string;
	appliedFilters?: { [key: string]: string };
	ignoreQueryParams?: boolean;
}) => {
	const [itemsList] = useState(() => {
		return proxy<TreeViewListItem[]>([]);
	});

	const [lastSelectedFolder] = useState(() => {
		return proxy<TreeViewContextType["lastSelectedFolder"]>({ value: undefined });
	});

	const [openedFolders] = useState(() => {
		return proxyMap<string, TreeViewOpenedFolder>();
	});

	const [selectedItemIds] = useState(() => {
		return proxy<{ values: string[] }>({ values: [] });
	});

	const [isSelecting] = useState(() => {
		return proxy<{ value: boolean }>({
			value: true,
		});
	});

	return (
		<TreeViewContext.Provider
			value={{
				itemsList,
				openedFolders,
				fetchRootLayer,
				fetchFolderLayer,
				lastSelectedFolder,
				selectedItemIds,
				isSelecting,
				canSwitchView,
				rootFolderId,
				appliedFilters: appliedFilters ?? {},
				ignoreQueryParams,
			}}
		>
			{children}
		</TreeViewContext.Provider>
	);
};

export const useTreeViewContext = () => {
	const context = useContext(TreeViewContext);
	if (!context) {
		throw new Error("useTreeViewContext must be used within a TreeViewProvider");
	}
	return context;
};
